import { ChevronDown, ChevronUp, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { Contract, ContractUpsertInput } from "../types/contract";

interface ContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contract: ContractUpsertInput) => Promise<void>;
  contract: Contract | null;
  mode: "create" | "edit";
}

const ContractModal: React.FC<ContractModalProps> = ({
  isOpen,
  onClose,
  onSave,
  contract,
  mode,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOptional, setShowOptional] = useState(false);

  // Required fields
  const [conId, setConId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [secType, setSecType] = useState("STK");
  const [currency, setCurrency] = useState("USD");
  const [exchange, setExchange] = useState("SMART");
  const [primaryExchange, setPrimaryExchange] = useState("");
  const [longName, setLongName] = useState("");

  // Optional fields
  const [isin, setIsin] = useState("");
  const [timeZoneId, setTimeZoneId] = useState("");
  const [validExchanges, setValidExchanges] = useState("");
  const [minTick, setMinTick] = useState("");
  const [minSize, setMinSize] = useState("");
  const [sizeIncrement, setSizeIncrement] = useState("");
  const [optionAllowed, setOptionAllowed] = useState<boolean | null>(null);
  const [optionExchange, setOptionExchange] = useState("");
  const [optionType, setOptionType] = useState("");
  const [yahooTickerName, setYahooTickerName] = useState("");
  const [figiComposite, setFigiComposite] = useState("");

  useEffect(() => {
    if (contract && mode === "edit") {
      setConId(String(contract.conId));
      setSymbol(contract.symbol);
      setSecType(contract.secType);
      setCurrency(contract.currency);
      setExchange(contract.exchange);
      setPrimaryExchange(contract.primaryExchange);
      setLongName(contract.longName);
      setIsin(contract.isin || "");
      setTimeZoneId(contract.timeZoneId || "");
      setValidExchanges(contract.validExchanges || "");
      setMinTick(contract.minTick != null ? String(contract.minTick) : "");
      setMinSize(contract.minSize != null ? String(contract.minSize) : "");
      setSizeIncrement(contract.sizeIncrement != null ? String(contract.sizeIncrement) : "");
      setOptionAllowed(contract.optionAllowed);
      setOptionExchange(contract.optionExchange || "");
      setOptionType(contract.optionType || "");
      setYahooTickerName(contract.yahooTickerName || "");
      setFigiComposite(contract.figiComposite || "");
    } else {
      // Reset for create mode
      setConId("");
      setSymbol("");
      setSecType("STK");
      setCurrency("USD");
      setExchange("SMART");
      setPrimaryExchange("");
      setLongName("");
      setIsin("");
      setTimeZoneId("");
      setValidExchanges("");
      setMinTick("");
      setMinSize("");
      setSizeIncrement("");
      setOptionAllowed(null);
      setOptionExchange("");
      setOptionType("");
      setYahooTickerName("");
      setFigiComposite("");
    }
    setError(null);
    setShowOptional(false);
  }, [contract, mode, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!conId || !symbol || !secType || !currency || !exchange || !primaryExchange || !longName) {
      setError("Please fill in all required fields");
      return;
    }

    const conIdNum = parseInt(conId, 10);
    if (isNaN(conIdNum)) {
      setError("Con ID must be a valid number");
      return;
    }

    try {
      setSaving(true);
      const input: ContractUpsertInput = {
        conId: conIdNum,
        symbol: symbol.trim(),
        secType: secType.trim(),
        currency: currency.trim(),
        exchange: exchange.trim(),
        primaryExchange: primaryExchange.trim(),
        longName: longName.trim(),
        isin: isin.trim() || null,
        timeZoneId: timeZoneId.trim() || null,
        validExchanges: validExchanges.trim() || null,
        minTick: minTick ? parseFloat(minTick) : null,
        minSize: minSize ? parseFloat(minSize) : null,
        sizeIncrement: sizeIncrement ? parseFloat(sizeIncrement) : null,
        optionAllowed,
        optionExchange: optionExchange.trim() || null,
        optionType: optionType.trim() || null,
        yahooTickerName: yahooTickerName.trim() || null,
        figiComposite: figiComposite.trim() || null,
      };

      await onSave(input);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save contract");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-1 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {mode === "create" ? "Add Contract" : "Edit Contract"}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition-colors ${
              isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Required Fields */}
          <div className="space-y-4">
            <h3
              className={`text-sm font-medium uppercase tracking-wider ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Required Fields
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Con ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={conId}
                  onChange={(e) => setConId(e.target.value)}
                  className={inputClass}
                  placeholder="e.g., 265598"
                  disabled={mode === "edit"}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Symbol <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  className={inputClass}
                  placeholder="e.g., AAPL"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Security Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={secType}
                  onChange={(e) => setSecType(e.target.value)}
                  className={inputClass}
                >
                  <option value="STK">STK (Stock)</option>
                  <option value="OPT">OPT (Option)</option>
                  <option value="FUT">FUT (Future)</option>
                  <option value="ETF">ETF</option>
                  <option value="CASH">CASH (Forex)</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  Currency <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  className={inputClass}
                  placeholder="e.g., USD"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Exchange <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={exchange}
                  onChange={(e) => setExchange(e.target.value.toUpperCase())}
                  className={inputClass}
                  placeholder="e.g., SMART"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Primary Exchange <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={primaryExchange}
                  onChange={(e) => setPrimaryExchange(e.target.value.toUpperCase())}
                  className={inputClass}
                  placeholder="e.g., NASDAQ"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                Long Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={longName}
                onChange={(e) => setLongName(e.target.value)}
                className={inputClass}
                placeholder="e.g., Apple Inc"
              />
            </div>
          </div>

          {/* Optional Fields Toggle */}
          <button
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            className={`mt-6 flex items-center gap-2 text-sm font-medium ${
              isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"
            }`}
          >
            {showOptional ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showOptional ? "Hide" : "Show"} Optional Fields
          </button>

          {/* Optional Fields */}
          {showOptional && (
            <div className="mt-4 space-y-4">
              <h3
                className={`text-sm font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Optional Fields
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>ISIN</label>
                  <input
                    type="text"
                    value={isin}
                    onChange={(e) => setIsin(e.target.value.toUpperCase())}
                    className={inputClass}
                    placeholder="e.g., US0378331005"
                  />
                </div>

                <div>
                  <label className={labelClass}>FIGI Composite</label>
                  <input
                    type="text"
                    value={figiComposite}
                    onChange={(e) => setFigiComposite(e.target.value.toUpperCase())}
                    className={inputClass}
                    placeholder="e.g., BBG000B9XRY4"
                    maxLength={25}
                  />
                </div>

                <div>
                  <label className={labelClass}>Timezone</label>
                  <input
                    type="text"
                    value={timeZoneId}
                    onChange={(e) => setTimeZoneId(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., America/New_York"
                  />
                </div>

                <div>
                  <label className={labelClass}>Valid Exchanges</label>
                  <input
                    type="text"
                    value={validExchanges}
                    onChange={(e) => setValidExchanges(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., NASDAQ,NYSE"
                  />
                </div>

                <div>
                  <label className={labelClass}>Yahoo Ticker</label>
                  <input
                    type="text"
                    value={yahooTickerName}
                    onChange={(e) => setYahooTickerName(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., AAPL"
                  />
                </div>

                <div>
                  <label className={labelClass}>Min Tick</label>
                  <input
                    type="number"
                    step="any"
                    value={minTick}
                    onChange={(e) => setMinTick(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., 0.01"
                  />
                </div>

                <div>
                  <label className={labelClass}>Min Size</label>
                  <input
                    type="number"
                    step="any"
                    value={minSize}
                    onChange={(e) => setMinSize(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., 1"
                  />
                </div>

                <div>
                  <label className={labelClass}>Size Increment</label>
                  <input
                    type="number"
                    step="any"
                    value={sizeIncrement}
                    onChange={(e) => setSizeIncrement(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., 1"
                  />
                </div>

                <div>
                  <label className={labelClass}>Options Allowed</label>
                  <select
                    value={optionAllowed === null ? "" : String(optionAllowed)}
                    onChange={(e) =>
                      setOptionAllowed(e.target.value === "" ? null : e.target.value === "true")
                    }
                    className={inputClass}
                  >
                    <option value="">Not Set</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Option Exchange</label>
                  <input
                    type="text"
                    value={optionExchange}
                    onChange={(e) => setOptionExchange(e.target.value.toUpperCase())}
                    className={inputClass}
                    placeholder="e.g., CBOE"
                  />
                </div>

                <div>
                  <label className={labelClass}>Option Type</label>
                  <input
                    type="text"
                    value={optionType}
                    onChange={(e) => setOptionType(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., STK"
                  />
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div
          className={`flex items-center justify-end gap-3 p-4 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-600 hover:bg-gray-100"
            } disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractModal;

